import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { auth0Config } from 'src/config';
import { paths } from 'src/paths';
import type { User } from 'src/types/user';
import { Issuer } from 'src/utils/auth';
import { initSupabaseLogger, setLoggerUser } from 'src/api/supabase/supabase-logger';

type UserRole = 'admin' | 'salesman';

function getUserRole(email: string | undefined): UserRole | undefined {
  return email ? 'admin' : undefined;
}

function isAllowedUser(email: string | undefined): boolean {
  return Boolean(email);
}

// Extract domain from issuer_base_url (remove https:// prefix if present)
const auth0Domain = (auth0Config.issuer_base_url || '').replace(/^https?:\/\//, '');

// Debug: Log Auth0 configuration
// console.log('Auth0 Config:', {
//   domain: auth0Domain,
//   clientId: auth0Config.client_id,
//   baseUrl: auth0Config.base_url,
//   redirectUri: auth0Config.base_url + paths.auth.auth0.callback
// });

// Validate Auth0 configuration
const isAuth0Configured = Boolean(auth0Domain && auth0Config.client_id);

if (!isAuth0Configured) {
  // console.error('Auth0 configuration is missing. Check your .env file.');
}

// Only create Auth0 client if properly configured
let auth0Client: Auth0Client | null = null;
try {
  if (isAuth0Configured) {
    auth0Client = new Auth0Client({
      domain: auth0Domain,
      clientId: auth0Config.client_id!,
      cacheLocation: 'localstorage',
      authorizationParams: {
        redirect_uri: auth0Config.base_url + paths.auth.auth0.callback,
        scope: 'openid profile email'
      }
    });
  }
} catch (err) {
  // console.error('Failed to create Auth0 client:', err);
}

type AppState = {
  returnTo?: string;
};

interface State {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User | null;
}

enum ActionType {
  INITIALIZE = 'INITIALIZE',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
}

type InitializeAction = {
  type: ActionType.INITIALIZE;
  payload: {
    isAuthenticated: boolean;
    user: User | null;
  };
};

type LoginAction = {
  type: ActionType.LOGIN;
  payload: {
    user: User;
  };
};

type LogoutAction = {
  type: ActionType.LOGOUT;
}

type Action =
  | InitializeAction
  | LoginAction
  | LogoutAction;

type Handler = (state: State, action: any) => State;

const initialState: State = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

// Initialize the Supabase auto-logger once at module load
initSupabaseLogger();

const handlers: Record<ActionType, Handler> = {
  INITIALIZE: (state: State, action: InitializeAction): State => {
    const { isAuthenticated, user } = action.payload;

    // Keep the Supabase auto-logger in sync with the current user
    setLoggerUser(user?.email, user?.role);

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  },
  LOGIN: (state: State, action: LoginAction): State => {
    const { user } = action.payload;

    setLoggerUser(user?.email, user?.role);

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state: State): State => {
    setLoggerUser(null, null);

    return {
      ...state,
      isAuthenticated: false,
      user: null
    };
  }
};

const reducer = (state: State, action: Action): State => (
  handlers[action.type] ? handlers[action.type](state, action) : state
);

export interface AuthContextType extends State {
  issuer: Issuer.Auth0;
  loginWithRedirect: (appState?: AppState) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  handleRedirectCallback: () => Promise<AppState | undefined>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  ...initialState,
  issuer: Issuer.Auth0,
  loginWithRedirect: () => Promise.resolve(),
  loginWithPassword: () => Promise.resolve(),
  handleRedirectCallback: () => Promise.resolve(undefined),
  logout: () => Promise.resolve()
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(
    async (): Promise<void> => {
      // If Auth0 client is not configured, just initialize as unauthenticated
      if (!auth0Client) {
        // console.warn('Auth0 client not configured, initializing as unauthenticated');
        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
        return;
      }

      try {
        // Check for persisted password-grant session first
        const storedUser = localStorage.getItem('auth_user');
        const tokenExpiry = localStorage.getItem('auth_token_expiry');

        if (storedUser && tokenExpiry && Date.now() < Number(tokenExpiry)) {
          const user: User = JSON.parse(storedUser);

          // Verify the stored user is still allowed & re-derive role
          const storedRole = getUserRole(user.email);
          if (storedRole) {
            user.role = storedRole;
            dispatch({
              type: ActionType.INITIALIZE,
              payload: {
                isAuthenticated: true,
                user
              }
            });
            return;
          }
          // If not allowed, clear stored data
          localStorage.removeItem('auth_user');
          localStorage.removeItem('auth_access_token');
          localStorage.removeItem('auth_token_expiry');
        }

        // Fall back to Auth0 SDK session check
        await auth0Client.checkSession();

        const isAuthenticated = await auth0Client.isAuthenticated();

        if (isAuthenticated) {
          const user = await auth0Client.getUser();

          // Check if user email is allowed
          if (!isAllowedUser(user?.email)) {
            await auth0Client.logout({ logoutParams: { returnTo: window.location.origin } });
            dispatch({
              type: ActionType.INITIALIZE,
              payload: {
                isAuthenticated: false,
                user: null
              }
            });
            return;
          }

          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated,
              user: {
                id: user!.sub as string,
                avatar: user!.picture,
                email: user!.email as string,
                name: user!.name || user!.nickname || user!.email as string,
                role: getUserRole(user!.email),
                plan: 'Premium'
              }
            }
          });
        } else {
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated,
              user: null
            }
          });
        }
      } catch (err) {
        // console.error(err);
        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    },
    [dispatch]
  );

  useEffect(
    () => {
      initialize();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const loginWithRedirect = useCallback(
    async (appState?: AppState): Promise<void> => {
      if (!auth0Client) {
        // console.error('Auth0 client not configured');
        return;
      }
      await auth0Client.loginWithRedirect({
        appState,
        authorizationParams: {
          prompt: 'login',  // Skip consent screen for first-party apps
          screen_hint: 'login'  // Hide signup option, show login only
        }
      });
    },
    []
  );

  const loginWithPassword = useCallback(
    async (email: string, password: string): Promise<void> => {
      if (!auth0Client) {
        throw new Error('Auth0 client not configured');
      }

      if (!isAllowedUser(email)) {
        throw new Error('Unauthorized email address');
      }

      const response = await fetch(`https://${auth0Domain}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grant_type: 'password',
          client_id: auth0Config.client_id,
          username: email,
          password,
          connection: 'Username-Password-Authentication',
          scope: 'openid profile email',
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        console.error('Auth0 login error:', data);
        throw new Error(data.error_description || data.message || 'Invalid email or password');
      }

      const tokens = await response.json();

      // Use the access_token to fetch user info from Auth0
      const userInfoResponse = await fetch(`https://${auth0Domain}/userinfo`, {
        headers: { Authorization: `Bearer ${tokens.access_token}` },
      });

      if (!userInfoResponse.ok) {
        throw new Error('Failed to retrieve user info');
      }

      const userInfo = await userInfoResponse.json();

      // Check if user email is allowed
      if (!isAllowedUser(userInfo.email)) {
        throw new Error('Unauthorized email address');
      }

      const user: User = {
        id: userInfo.sub as string,
        avatar: userInfo.picture,
        email: userInfo.email as string,
        name: userInfo.name || userInfo.nickname || userInfo.email as string,
        role: getUserRole(userInfo.email),
        plan: 'Premium'
      };

      // Persist auth data so session survives page reloads
      localStorage.setItem('auth_user', JSON.stringify(user));
      localStorage.setItem('auth_access_token', tokens.access_token);
      if (tokens.expires_in) {
        localStorage.setItem('auth_token_expiry', String(Date.now() + tokens.expires_in * 1000));
      }

      dispatch({
        type: ActionType.LOGIN,
        payload: { user }
      });
    },
    []
  );

  const handleRedirectCallback = useCallback(
    async (): Promise<AppState | undefined> => {
      if (!auth0Client) {
        // console.error('Auth0 client not configured');
        return undefined;
      }
      // console.log('handleRedirectCallback: Starting...');
      const result = await auth0Client.handleRedirectCallback();
      // console.log('handleRedirectCallback: Got result', result);
      const user = await auth0Client.getUser();
      // console.log('handleRedirectCallback: Got user', user);

      // Check if user email is allowed
      if (!isAllowedUser(user?.email)) {
        // Unauthorized user - log them out
        await auth0Client.logout({ logoutParams: { returnTo: window.location.origin } });
        dispatch({
          type: ActionType.LOGOUT
        });
        return undefined;
      }

      dispatch({
        type: ActionType.LOGIN,
        payload: {
          user: {
            id: user!.sub as string,
            avatar: user!.picture,
            email: user!.email as string,
            name: user!.name || user!.nickname || user!.email as string,
            role: getUserRole(user!.email),
            plan: 'Premium'
          }
        }
      });

      return result.appState;
    },
    []
  );

  const logout = useCallback(
    async (): Promise<void> => {
      // Clear persisted password-grant session
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_access_token');
      localStorage.removeItem('auth_token_expiry');

      if (!auth0Client) {
        dispatch({
          type: ActionType.LOGOUT
        });
        return;
      }
      await auth0Client.logout();
      dispatch({
        type: ActionType.LOGOUT
      });
    },
    []
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        issuer: Issuer.Auth0,
        loginWithRedirect,
        loginWithPassword,
        handleRedirectCallback,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const AuthConsumer = AuthContext.Consumer;
